import type {
  TransactionFailureReasonResource,
  TransactionFlagResource,
  TransactionSourceTypeResource,
  TransactionTypeResource,
  UserTransactionReasonResource,
} from '@/features/payouts/api/resources/transactions'
import type { TransactionType } from './transaction-type'
import type { TransactionSourceType } from './transaction-source-type'
import type { TransactionFlag } from './transaction-flag'
import type { TransactionFailureReason } from './transaction-failure-reason'
import type { UserTransactionReason } from './user-transaction-reason'
import { match } from '@/utils/pattern-match-util'

/**
 * Maps whether a transaction pays or charges the recipient.
 */
export function mapTransactionTypeResourceToTransactionType(
  resource: TransactionTypeResource,
): TransactionType {
  return match(resource, {
    PAYMENT: 'Payment',
    ABATEMENT: 'Abatement',
  })
}

/**
 * Maps a transaction type to its API value.
 */
export function mapTransactionTypeToResource(
  type: TransactionType,
): TransactionTypeResource {
  switch (type) {
    case 'Payment':
      return 'PAYMENT'
    case 'Abatement':
      return 'ABATEMENT'
  }
}

/**
 * Maps what created a transaction.
 */
export function mapTransactionSourceTypeResourceToTransactionSourceType(
  resource: TransactionSourceTypeResource,
): TransactionSourceType {
  return match(resource, {
    UNSPECIFIED: 'Unknown',
    USER: 'User',
    JOB: 'Job',
  })
}

/**
 * Maps a transaction source to its API value.
 */
export function mapTransactionSourceTypeToResource(
  sourceType: TransactionSourceType,
): TransactionSourceTypeResource {
  switch (sourceType) {
    case 'User':
      return 'USER'
    case 'Job':
      return 'JOB'
    case 'Unknown':
      throw new Error(`Cannot map unknown transaction source type`)
  }
}

/**
 * Maps why a user transaction was entered.
 */
export function mapUserTransactionReasonResourceToUserTransactionReason(
  resource: UserTransactionReasonResource,
): UserTransactionReason {
  return match(resource, {
    BACKGROUND_CHECK: 'BackgroundCheck',
    PENALTY: 'Penalty',
    BONUS: 'Bonus',
    MISC_PAYMENT: 'MiscPayment',
    MISC_ABATEMENT: 'MiscAbatement',
  })
}

/**
 * Maps a user transaction reason to its API value.
 */
export function mapUserTransactionReasonToResource(
  reason: UserTransactionReason,
): UserTransactionReasonResource {
  switch (reason) {
    case 'BackgroundCheck':
      return 'BACKGROUND_CHECK'
    case 'Penalty':
      return 'PENALTY'
    case 'Bonus':
      return 'BONUS'
    case 'MiscPayment':
      return 'MISC_PAYMENT'
    case 'MiscAbatement':
      return 'MISC_ABATEMENT'
    case 'Unknown':
      throw new Error(`Cannot map unknown transaction reason`)
  }
}

/**
 * Maps a flag raised on a transaction.
 */
export function mapTransactionFlagResourceToTransactionFlag(
  resource: TransactionFlagResource,
): TransactionFlag {
  return match(resource, {
    JOB_CANCELED: 'JobCanceled',
    JOB_PAYOUT_AUTO_ADJUSTED: 'JobPayoutAutoAdjusted',
  })
}

/**
 * Maps a transaction flag to its API value.
 */
export function mapTransactionFlagToResource(
  flag: TransactionFlag,
): TransactionFlagResource {
  switch (flag) {
    case 'JobCanceled':
      return 'JOB_CANCELED'
    case 'JobPayoutAutoAdjusted':
      return 'JOB_PAYOUT_AUTO_ADJUSTED'
    case 'Unknown':
      throw new Error(`Cannot map unknown transaction flag`)
  }
}

/**
 * Maps why a transaction last failed.
 */
export function mapTransactionFailureReasonResourceToTransactionFailureReason(
  resource: TransactionFailureReasonResource,
): TransactionFailureReason {
  return match(resource, {
    MISSING_PAYOUT_METHOD: 'MissingPayoutMethod',
    PAYOUT_GATEWAY_ERROR: 'PayoutGatewayError',
  })
}
